import { scaleSequential } from 'd3-scale'
import { interpolateRgbBasis } from 'd3-interpolate'
import { formatDate } from 'lib/string'
import { TimePeriod } from 'api/pulse'
import {
  endOfDay,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  subDays,
  subWeeks,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  format,
  startOfYear,
  endOfYear,
  subYears,
  subQuarters,
  parseISO,
} from 'date-fns'

const workWhileInterpolator = interpolateRgbBasis([
  '#A9EEDF',
  '#F8B400',
  '#E56B6F',
  '#6B5B95',
  '#0052AE',
])

export function getColorScale(n: number) {
  const colorScale = scaleSequential()
    .domain([0, n - 1])
    .interpolator(workWhileInterpolator)
  return (i: number) => colorScale(i)
}

export function formatTimePeriodDate(timePeriod: TimePeriod, date: string) {
  switch (timePeriod) {
    case 'daily':
      return formatDate(date, 'MMM dd, yyyy')
    case 'weekly': {
      const parsedDate = parseISO(date)
      const startOfCurrentWeek = startOfWeek(parsedDate, { weekStartsOn: 0 })
      const endOfCurrentWeek = endOfWeek(parsedDate, { weekStartsOn: 0 })

      return `${format(startOfCurrentWeek, 'MMM dd, yyyy')} - ${format(
        endOfCurrentWeek,
        'MMM dd, yyyy'
      )}`
    }
    case 'monthly':
      return formatDate(date, 'MMM yyyy')
    case 'quarterly':
      return formatDate(date, 'QQQ yyyy')
    case 'yearly':
      return formatDate(date, 'yyyy')
    default:
      return formatDate(date, 'MMM d, yyyy')
  }
}

export const getDateRangeFilterPresets = () => {
  const today = new Date()

  return [
    {
      label: 'Today',
      value: {
        from: startOfDay(today),
        to: endOfDay(today),
      },
    },
    {
      label: 'Yesterday',
      value: {
        from: startOfDay(subDays(today, 1)),
        to: endOfDay(subDays(today, 1)),
      },
    },
    {
      label: 'This week',
      value: {
        from: startOfWeek(today, { weekStartsOn: 0 }),
        to: endOfWeek(today, { weekStartsOn: 0 }),
      },
    },
    {
      label: 'Last week',
      value: {
        from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 }),
        to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 0 }),
      },
    },
    {
      label: format(today, 'MMMM'), // this month
      value: {
        from: startOfMonth(today),
        to: endOfMonth(today),
      },
    },
    {
      label: format(subMonths(today, 1), 'MMMM'), // last month
      value: {
        from: startOfMonth(subMonths(today, 1)),
        to: endOfMonth(subMonths(today, 1)),
      },
    },
    {
      label: format(today, 'QQQ'), // this quarter
      value: {
        from: startOfQuarter(today),
        to: endOfQuarter(today),
      },
    },
    {
      label: format(subQuarters(today, 1), 'QQQ'), // last quarter
      value: {
        from: startOfQuarter(subQuarters(today, 1)),
        to: endOfQuarter(subQuarters(today, 1)),
      },
    },
    {
      label: format(today, 'yyyy'), // this year
      value: {
        from: startOfYear(today),
        to: endOfYear(today),
      },
    },
    {
      label: format(subYears(today, 1), 'yyyy'), // last year
      value: {
        from: startOfYear(subYears(today, 1)),
        to: endOfYear(subYears(today, 1)),
      },
    },
  ]
}
